import AppContext from "@common/contexts/App";
import Icon from "@common/controls/Icon/component";
import PillowList from "@common/controls/List/Pillow/component";
import PillowListItem from "@common/controls/List/Pillow/Item";
import Preloader from "@common/controls/Preloader/component";
import UploadDndFile from "@common/controls/Upload/DndFile";
import UploadLink from "@common/controls/Upload/Link";
import { type PendingFile, type UploadedFileInfo } from "@common/controls/Upload/types";
import { IconType, type Media, SystemColor } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import { getApiAdminPrefix } from "@common/lib/api";
import { removeItem } from "@common/lib/array";
import { usePromise } from "@common/lib/hooks";
import { updateQueryString } from "@common/lib/url";
import { getApi } from "@common/modules/system/api";
import classNames from "classnames";
import React from "react";
import { getMediaUrl } from "../../lib";
import { type UploadEndpointInfo } from "../types";
import "./style.less";

type UploadedMediaProps = {
  id: string;
  media: Media.Item | null;
  onDelete: () => void;
};
const UploadedMedia = (props: UploadedMediaProps) => {
  const i18n = useI18n();

  const api = React.useMemo(() => getApi(i18n), [i18n]);

  const [media, isLoading] = usePromise<Media.Item>({
    fn: api.getMedia,
    args: [props.id],
    shouldLoad: !props.media
  });

  // TODO: use isLoading instead. https://app.clickup.com/t/86bwb5y74
  const prepend = React.useMemo(
    () =>
      media || props.media ? (
        <Icon color={SystemColor.Blue} icon={IconType.Tick} />
      ) : (
        <Preloader type="spinner" isFontSize />
      ),
    [media, props.media]
  );

  const label = React.useMemo(() => {
    const m = props.media ?? media;

    return m ? (
      <a href={getMediaUrl(m.mediaSourceKey, m.sourceId)} download target="_blank">
        {m.name}
      </a>
    ) : (
      ""
    );
  }, [props.media, media]);

  if (!isLoading && !(props.media ?? media)) return null;

  return <PillowListItem className="media_list_item" prepend={prepend} label={label} onDeleteClick={props.onDelete} />;
};

type Props = UploadEndpointInfo & {
  isAbsolute?: boolean;
  isHost?: boolean;
  mode: "dropzone" | "link";
  onChange: (mediaIds: string[]) => void;
  onStartUploading?: () => void;
  /** whether the original file (no webp conversion) should be uploaded */
  original?: boolean;
  name?: string;
  style?: React.CSSProperties;
  value?: string[] | null;
};
const MediaUploaderList = (props: Props) => {
  const appContext = React.useContext(AppContext);

  const [uploadingItems, setUploadingItems] = React.useState<PendingFile[]>([]);
  const [uploadedItems, setUploadedItems] = React.useState<UploadedFileInfo<Media.Item>[]>([]);

  const onDelete = React.useCallback(
    (id: string) => {
      props.onChange(removeItem(props.value ?? [], (x) => x === id));
      setUploadedItems((x) => removeItem(x, (x) => x.mediaId === id));
    },
    [props.onChange]
  );

  const onUploadComplete = React.useCallback(
    (item: UploadedFileInfo<Media.Item>) => {
      setUploadingItems((x) => removeItem(x, (x) => x.id === item.id));
      props.onChange([...(props.value ?? []), item.mediaId]);
      setUploadedItems((x) => [...x, item]);
    },
    [props.onChange]
  );

  const onUploadAdded = React.useCallback(
    (file: PendingFile) => {
      props.onStartUploading && props.onStartUploading();

      setUploadingItems((x) => [...x, file]);
    },
    [props.onStartUploading]
  );

  const loadingPrepend = React.useMemo(() => <Preloader type="spinner" isFontSize />, []);

  const baseUrl = React.useMemo(() => {
    if (appContext.context === "next") return "/api/site/v1/media/upload";

    return `${getApiAdminPrefix(props.isHost)}/upload/media`;
  }, [props.isHost, appContext.context]);

  const endpoint = React.useMemo(
    () =>
      updateQueryString(baseUrl, {
        isAbsolute: props.isAbsolute,
        isAttachment: props.isAttachment,
        location: props.location ?? "Files",
        convertToWebp: !props.original,
        subPath: props.subPath
      }),
    [props.isAbsolute, props.isAttachment, props.location, props.original, props.subPath, baseUrl]
  );

  return (
    <div className={classNames("media_uploader_list", props.mode)} style={props.style}>
      {props.mode === "dropzone" && (
        <UploadDndFile
          isNoPreloader
          onStartUploading={onUploadAdded}
          onUploaded={onUploadComplete}
          url={endpoint}
          qa={props.name}
        />
      )}

      {(!!props.value?.length || !!uploadingItems.length) && (
        <PillowList className="uploads">
          {props.value?.map((x) => (
            <UploadedMedia
              key={x}
              id={x}
              media={uploadedItems.find((y) => y.mediaId === x) ?? null}
              onDelete={() => onDelete(x)}
            />
          ))}
          {uploadingItems.map((x) => (
            <PillowListItem key={x.id} className="media_list_item" prepend={loadingPrepend} label={x.name} />
          ))}
        </PillowList>
      )}

      {props.mode === "link" && (
        <UploadLink onStartUploading={onUploadAdded} onUploaded={onUploadComplete} url={endpoint} />
      )}
    </div>
  );
};

export default MediaUploaderList;
